import 'firebase/compat/auth';
import axios from 'axios';
import firebase from 'firebase/compat/app';
import ApiError from './ApiError';
import AUTH_PRESETS, { presetNeedsToken } from './authPresets';
import { getAwsApiBaseUrl } from 'helpers/getCompanyVariables';

// absolute paths are used as-is, which covers the endpoints that live outside the
// portal api (zoho proxy, cloud functions) without needing a separate helper
function resolveUrl(path, baseUrl) {
  if (/^https?:\/\//.test(path)) return path;
  return `${baseUrl}${path}`;
}

async function buildAuthHeaders(auth, forceRefresh) {
  const preset = AUTH_PRESETS[auth];
  if (!preset) {
    throw new ApiError({ message: `Unknown auth preset "${auth}"` });
  }
  if (!presetNeedsToken(auth)) return preset();

  const user = firebase.auth().currentUser;
  // never sign the user out from here — rendering that case is the caller's job
  if (!user) throw new ApiError({ message: 'Unauthorized', status: 401 });

  const idToken = await user.getIdToken(forceRefresh);
  return preset(idToken);
}

/**
 * single entry point for portal http calls. resolves with the response body and
 * throws ApiError on any non-2xx, so callers and TanStack Query can branch on status.
 * @param method - http verb, defaults to GET
 * @param path - api path, or an absolute url to bypass baseUrl
 * @param baseUrl - api origin, defaults to the active company's aws api
 * @param params - query string params
 * @param data - request body
 * @param auth - name of the auth preset used to build the headers
 * @param headers - extra headers, merged over the preset's
 * @param responseType - axios response type, e.g. 'blob' for downloads
 * @param signal - AbortSignal, wired automatically by useApiQuery
 * @param onUploadProgress - axios upload progress callback, for long multipart uploads
 */
export default async function apiRequest({
  method = 'GET',
  path = '',
  baseUrl = getAwsApiBaseUrl(),
  params,
  data,
  auth = 'aws',
  headers,
  responseType,
  signal,
  onUploadProgress
} = {}) {
  const url = resolveUrl(path, baseUrl);

  async function send(forceRefreshToken) {
    const authHeaders = await buildAuthHeaders(auth, forceRefreshToken);
    return axios({
      method,
      url,
      params,
      data,
      responseType,
      signal,
      onUploadProgress,
      headers: { ...authHeaders, ...headers },
      // resolve every status so failures surface as ApiError instead of axios errors
      validateStatus: () => true
    });
  }

  let response = await send(false);

  // an expired token looks exactly like a denied one, so force-refresh and retry
  // once before treating a 401 as final
  if (response.status === 401 && presetNeedsToken(auth)) {
    response = await send(true);
  }

  if (response.status >= 400) {
    const body = response.data;
    // enveloped modules nest the failure under `error`; the global express
    // handler sends a bare `{ message }`, so both shapes are read
    throw new ApiError({
      message: body?.error?.message ?? body?.message ?? 'Request failed',
      status: response.status,
      code: body?.error?.code ?? body?.code ?? null,
      details: body?.error?.details ?? null,
      body
    });
  }

  return response.data;
}
